import React, { useState, useEffect } from 'react';
import { getWeightForecast } from '../../services/api';

function WeightForecast() {
  const [targetWeight, setTargetWeight] = useState(100);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadForecast = async (target) => {
    if (!target || target <= 0) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getWeightForecast(target);
      if (data.error) {
        setError(data.error);
        setForecast(null);
      } else {
        setForecast(data);
      }
    } catch (err) {
      setError(err.message);
      setForecast(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadForecast(targetWeight);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    loadForecast(targetWeight);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="weight-forecast">
      <h3>Gewichts-Prognose</h3>

      <form onSubmit={handleSubmit} className="forecast-form">
        <label>
          Zielgewicht:
          <input
            type="number"
            value={targetWeight}
            onChange={(e) => setTargetWeight(Number(e.target.value))}
            min="50"
            max="200"
            step="0.5"
          />
          <span>kg</span>
        </label>
        <button type="submit" disabled={loading}>
          {loading ? 'Berechne...' : 'Berechnen'}
        </button>
      </form>

      {error && (
        <div className="forecast-error">{error}</div>
      )}

      {forecast && forecast.scenarios && (
        <div className="forecast-results">
          <div className="forecast-summary">
            <p>
              <strong>Aktuell:</strong> {forecast.current_weight} kg |
              <strong> Ziel:</strong> {forecast.target_weight} kg |
              <strong> Abzunehmen:</strong> {forecast.weight_to_lose} kg
            </p>
            <p className="forecast-basis">
              Basis: Ø {forecast.avg_deficit_30d} kcal/Tag
              {forecast.deficit_source === 'real'
                ? ' · echtes Körper-Defizit (Regression)'
                : ' · Tracking-Daten (Gewichtsdaten unzureichend)'}
              {' '}({forecast.days_analyzed} Tage)
            </p>
          </div>

          <div className="forecast-scenarios">
            {forecast.scenarios.current && (
              <div className="scenario current">
                <h4>Aktuelles Tempo</h4>
                <div className="scenario-date">{formatDate(forecast.scenarios.current.target_date)}</div>
                <div className="scenario-details">
                  <span>{forecast.scenarios.current.kg_per_week} kg/Woche</span>
                  <span>{forecast.scenarios.current.weeks_needed} Wochen</span>
                  <span>{forecast.scenarios.current.deficit_per_day} kcal/Tag</span>
                </div>
              </div>
            )}

            {forecast.scenarios.conservative && (
              <div className="scenario conservative">
                <h4>Konservativ (-20%)</h4>
                <div className="scenario-date">{formatDate(forecast.scenarios.conservative.target_date)}</div>
                <div className="scenario-details">
                  <span>{forecast.scenarios.conservative.kg_per_week} kg/Woche</span>
                  <span>{forecast.scenarios.conservative.weeks_needed} Wochen</span>
                  <span>{forecast.scenarios.conservative.deficit_per_day} kcal/Tag</span>
                </div>
              </div>
            )}

            {forecast.scenarios.optimistic && (
              <div className="scenario optimistic">
                <h4>Optimistisch (+20%)</h4>
                <div className="scenario-date">{formatDate(forecast.scenarios.optimistic.target_date)}</div>
                <div className="scenario-details">
                  <span>{forecast.scenarios.optimistic.kg_per_week} kg/Woche</span>
                  <span>{forecast.scenarios.optimistic.weeks_needed} Wochen</span>
                  <span>{forecast.scenarios.optimistic.deficit_per_day} kcal/Tag</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        .weight-forecast {
          background: var(--card-bg, white);
          border-radius: var(--radius-lg, 12px);
          padding: var(--space-6, 1.5rem);
          margin-top: var(--space-6, 1.5rem);
          box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1));
        }

        h3 {
          margin: 0 0 var(--space-4, 1rem) 0;
          color: var(--text-primary, #1f2937);
          font-weight: 600;
        }

        .forecast-form {
          display: flex;
          align-items: center;
          gap: var(--space-4, 1rem);
          margin-bottom: var(--space-4, 1rem);
          flex-wrap: wrap;
        }

        .forecast-form label {
          display: flex;
          align-items: center;
          gap: var(--space-2, 0.5rem);
          font-weight: 500;
          color: var(--text-secondary, #6b7280);
        }

        .forecast-form input {
          width: 85px;
          padding: var(--space-2, 0.5rem) var(--space-3, 0.75rem);
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: var(--radius-md, 8px);
          font-size: 1rem;
          background: var(--card-bg, white);
          color: var(--text-primary, #1f2937);
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .forecast-form input:focus {
          border-color: var(--primary-500, #3b82f6);
          box-shadow: 0 0 0 3px var(--primary-100, #dbeafe);
          outline: none;
        }

        .forecast-form button {
          padding: var(--space-2, 0.5rem) var(--space-5, 1.25rem);
          background: var(--primary-600, #2563eb);
          color: white;
          border: none;
          border-radius: var(--radius-md, 8px);
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 500;
          transition: background 0.2s, transform 0.15s;
        }

        .forecast-form button:hover:not(:disabled) {
          background: var(--primary-700, #1d4ed8);
          transform: translateY(-1px);
        }

        .forecast-form button:active:not(:disabled) {
          transform: translateY(0);
        }

        .forecast-form button:disabled {
          background: var(--text-muted, #9ca3af);
          cursor: not-allowed;
        }

        .forecast-error {
          background: var(--error-50, #fef2f2);
          color: var(--error-700, #b91c1c);
          padding: var(--space-3, 0.75rem) var(--space-4, 1rem);
          border-radius: var(--radius-md, 8px);
          margin-bottom: var(--space-4, 1rem);
          border-left: 4px solid var(--error-500, #ef4444);
        }

        .forecast-summary {
          margin-bottom: var(--space-4, 1rem);
          padding-bottom: var(--space-4, 1rem);
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }

        .forecast-summary p {
          margin: var(--space-1, 0.25rem) 0;
          color: var(--text-primary, #1f2937);
        }

        .forecast-summary strong {
          font-weight: 600;
        }

        .forecast-basis {
          font-size: 0.85rem;
          color: var(--text-muted, #9ca3af);
        }

        .forecast-scenarios {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: var(--space-4, 1rem);
        }

        .scenario {
          background: var(--bg-color, #f9fafb);
          border-radius: var(--radius-lg, 12px);
          padding: var(--space-5, 1.25rem);
          text-align: center;
          border: 2px solid var(--border-color, #e5e7eb);
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .scenario:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1));
        }

        .scenario.current {
          border-color: var(--primary-500, #3b82f6);
          background: var(--primary-50, #eff6ff);
        }

        .scenario.conservative {
          border-color: var(--text-muted, #9ca3af);
        }

        .scenario.optimistic {
          border-color: var(--success-500, #22c55e);
          background: var(--success-50, #f0fdf4);
        }

        .scenario h4 {
          margin: 0 0 var(--space-2, 0.5rem) 0;
          font-size: 0.85rem;
          color: var(--text-muted, #9ca3af);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-weight: 600;
        }

        .scenario-date {
          font-size: 1.35rem;
          font-weight: 700;
          color: var(--text-primary, #1f2937);
          margin-bottom: var(--space-2, 0.5rem);
        }

        .scenario.current .scenario-date {
          color: var(--primary-600, #2563eb);
        }

        .scenario.optimistic .scenario-date {
          color: var(--success-600, #16a34a);
        }

        .scenario-details {
          display: flex;
          flex-direction: column;
          gap: var(--space-1, 0.25rem);
          font-size: 0.8rem;
          color: var(--text-muted, #9ca3af);
        }
      `}</style>
    </div>
  );
}

export default WeightForecast;
